import React, { useMemo, useState } from "react";
import { View, Text, Pressable, ImageBackground } from "react-native";
import { BarChart2, ChevronRight } from "lucide-react-native";
import { CommonActions, useNavigation } from "@react-navigation/native";
import type { Clinic } from "@louise/clinics/clinics.types";
import type { ProviderOutcomes } from "@louise/providers/providers.types";
import type { PredictionResult } from "@louise/clinics/prediction/ivfPredictionEngine";
import { truncateAtComma } from "@louise/clinics/utils/clinicUtils";
import { EMBER } from "../../../onboarding/theme";
import {
  EmptyStateSubtitleText,
  GlassButton,
  MoonDivider,
  SupportingSubtitleText,
} from "@louise/moon-ui";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
type Props = {
  clinic: Clinic;
  prediction?: PredictionResult | null;
  /** Published rates that do not map to the standard four age bands. */
  publishedRates?: ProviderOutcomes["successRates"];
  /** Optional benchmark for published rates, such as the French ABM average. */
  publishedNationalAverage?: number;
  /** Per-age-band source location labels (for network best-of charts) */
  locationByBand?: Record<string, string | undefined>;
  /** Hide the info button (e.g. on secondary comparison charts) */
  hideInfo?: boolean;
};

const AGE_BANDS = [
  { key: "under35", label: "<35" },
  { key: "between35And37", label: "35-37" },
  { key: "between38And40", label: "38-40" },
  { key: "over42", label: "42+" },
] as const;

const NATIONAL_AVERAGE: Record<(typeof AGE_BANDS)[number]["key"], number> = {
  under35: 45,
  between35And37: 35,
  between38And40: 25,
  over42: 5,
};

const velvetCopper =
  require("@louise/moon-ui/assets/images/velvet-copper-btn.jpg") as number;

export default function ClinicSuccessChart({
  clinic,
  publishedRates,
  publishedNationalAverage,
  ...props
}: Props) {
  const clinicName = truncateAtComma(clinic.name);
  if (publishedRates?.some((rate) => rate.rate > 0)) {
    return (
      <PublishedRatesChart
        clinicName={clinicName}
        rates={publishedRates}
        nationalAverage={publishedNationalAverage}
      />
    );
  }
  return <AgeBandSuccessChart clinic={clinic} {...props} />;
}

function AgeBandSuccessChart({
  clinic,
  prediction,
  locationByBand,
  hideInfo,
}: Props) {
  const t = useTranslations();
  const clinicName = truncateAtComma(clinic.name);
  const successRates = clinic.successRates;
  const capturePress = usePressCapture("AgeBandSuccessChart");
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const hasClinicData = useMemo(() => {
    if (!successRates) return false;
    return AGE_BANDS.some(({ key }) => {
      const val = successRates[key];
      return typeof val === "number" && !Number.isNaN(val);
    });
  }, [successRates]);

  const chartData = useMemo(() => {
    return AGE_BANDS.map(({ key, label }) => {
      const natVal = NATIONAL_AVERAGE[key];
      const clinicVal = successRates?.[key];
      const hasClinicValue =
        typeof clinicVal === "number" &&
        !Number.isNaN(clinicVal) &&
        clinicVal > 0;
      return {
        key,
        label,
        national: natVal,
        clinic: hasClinicValue ? clinicVal : natVal,
        hasClinicData: hasClinicValue,
        location: locationByBand?.[key],
      };
    });
  }, [successRates, locationByBand]);

  const maxValue = 60;
  const hasLocations = !!locationByBand;
  const xAxisHeight = hasLocations ? 34 : 20;
  const chartHeight = hasLocations ? 134 : 120;
  const barAreaHeight = chartHeight - xAxisHeight;

  return (
    <View style={{ overflow: "hidden" }}>
      {/* Chart */}
      <View style={{ flexDirection: "row", gap: 8, marginBottom: 8 }}>
        {/* Y-Axis */}
        <View
          style={{
            justifyContent: "space-between",
            paddingBottom: xAxisHeight,
            height: chartHeight,
          }}
        >
          {[60, 40, 20, 0].map((value) => (
            <Text
              key={value}
              className="font-dm-sans"
              style={{
                fontSize: 10,
                fontWeight: "500",
                color: EMBER.colors.textFaint,
                textAlign: "right",
                width: 28,
              }}
            >
              {value}%
            </Text>
          ))}
        </View>

        {/* Chart Area */}
        <View style={{ flex: 1, height: chartHeight, position: "relative" }}>
          {/* Grid Lines */}
          <View
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: xAxisHeight,
              justifyContent: "space-between",
            }}
          >
            {[0, 1, 2, 3].map((i) => (
              <MoonDivider key={i} color="rgba(0,0,0,0.06)" />
            ))}
          </View>

          {/* Bars */}
          <View
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: xAxisHeight,
              flexDirection: "row",
              justifyContent: "space-around",
              alignItems: "flex-end",
              paddingHorizontal: 4,
            }}
          >
            {chartData.map((data, index) => {
              const nationalHeight = (data.national / maxValue) * barAreaHeight;
              const clinicHeight = (data.clinic / maxValue) * barAreaHeight;
              const isSelected = selectedIndex === index;

              return (
                <Pressable
                  key={data.key}
                  onPress={() => {
                    capturePress(pressLabel(data.label), "success_rate_bar");
                    setSelectedIndex(isSelected ? null : index);
                  }}
                  onHoverIn={() => setSelectedIndex(index)}
                  onHoverOut={() => setSelectedIndex(null)}
                  style={{
                    flex: 1,
                    alignItems: "center",
                    justifyContent: "flex-end",
                    height: "100%",
                  }}
                >
                  {/* Tooltip */}
                  {isSelected && (
                    <View
                      style={{
                        position: "absolute",
                        top: Math.max(
                          0,
                          Math.min(
                            (1 -
                              Math.max(data.clinic, data.national) / maxValue) *
                              barAreaHeight -
                              36,
                            barAreaHeight - 60,
                          ),
                        ),
                        backgroundColor: EMBER.colors.text,
                        borderRadius: 6,
                        paddingHorizontal: 8,
                        paddingVertical: 4,
                        zIndex: 10,
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 10,
                          fontWeight: "600",
                          color: "#FFFFFF",
                          textAlign: "center",
                        }}
                      >
                        {data.hasClinicData
                          ? `${data.clinic.toFixed(1)}%`
                          : t("access.success_chart.not_reported")}
                      </Text>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 9,
                          color: "rgba(255,255,255,0.6)",
                          textAlign: "center",
                        }}
                      >
                        {data.location
                          ? data.location
                          : t("access.success_chart.avg_value", {
                              params: { value: String(data.national) },
                            })}
                      </Text>
                    </View>
                  )}
                  <View
                    style={{
                      flexDirection: "row",
                      gap: 3,
                      alignItems: "flex-end",
                    }}
                  >
                    {/* National Average Bar */}
                    <View
                      style={{
                        width: 10,
                        height: nationalHeight,
                        borderTopLeftRadius: 2,
                        borderTopRightRadius: 2,
                        backgroundColor: isSelected
                          ? "rgba(0,0,0,0.18)"
                          : "rgba(0,0,0,0.1)",
                      }}
                    />
                    {/* Clinic Bar or "?" placeholder */}
                    {data.hasClinicData ? (
                      <ImageBackground
                        source={velvetCopper}
                        resizeMode="cover"
                        imageStyle={{
                          borderTopLeftRadius: 2,
                          borderTopRightRadius: 2,
                        }}
                        style={{
                          width: 10,
                          height: clinicHeight,
                          borderTopLeftRadius: 2,
                          borderTopRightRadius: 2,
                          overflow: "hidden",
                          opacity: isSelected ? 1 : 0.85,
                        }}
                      />
                    ) : (
                      <View
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: 7,
                          backgroundColor: "rgba(0,0,0,0.06)",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: -1,
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 9,
                            fontWeight: "600",
                            color: EMBER.colors.textFaint,
                          }}
                        >
                          ?
                        </Text>
                      </View>
                    )}
                  </View>
                </Pressable>
              );
            })}
          </View>

          {/* X-Axis Labels */}
          <View
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              flexDirection: "row",
              justifyContent: "space-around",
            }}
          >
            {chartData.map((data) => (
              <View key={data.key} style={{ flex: 1, alignItems: "center" }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 10,
                    fontWeight: "500",
                    color: EMBER.colors.textFaint,
                    textAlign: "center",
                  }}
                >
                  {data.label}
                </Text>
                {data.location && (
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 8,
                      color: EMBER.colors.textFaint,
                      textAlign: "center",
                      marginTop: 1,
                    }}
                    numberOfLines={1}
                  >
                    {data.location}
                  </Text>
                )}
              </View>
            ))}
          </View>
        </View>
      </View>

      {/* Legend */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
          paddingTop: 10,
          marginTop: 4,
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <ImageBackground
            source={velvetCopper}
            resizeMode="cover"
            imageStyle={{ borderRadius: 4 }}
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              overflow: "hidden",
            }}
          />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              fontWeight: "600",
              color: EMBER.colors.text,
            }}
          >
            {clinicName}
          </Text>
        </View>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <View
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              backgroundColor: "rgba(0,0,0,0.12)",
            }}
          />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              fontWeight: "500",
              color: EMBER.colors.textFaint,
            }}
          >
            {t("access.success_chart.national_average")}
          </Text>
        </View>
      </View>

      {/* Personalized estimate rendered separately in ClinicDetailScreen */}

      {/* Blur overlay when no data */}
      {!hasClinicData && (
        <View
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: 12,
            backgroundColor: "rgba(255,255,255,0.6)",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <BarChart2
            size={28}
            color={EMBER.colors.textFaint}
            strokeWidth={1.5}
          />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              fontWeight: "600",
              color: EMBER.colors.textMid,
              marginTop: 10,
            }}
          >
            {t("access.success_chart.unavailable_title")}
          </Text>
          <EmptyStateSubtitleText style={{ marginTop: 4, textAlign: "center" }}>
            {t("access.success_chart.unavailable_body")}
          </EmptyStateSubtitleText>
        </View>
      )}
    </View>
  );
}

function PublishedRatesChart({
  clinicName,
  rates,
  nationalAverage,
}: {
  clinicName: string;
  rates: ProviderOutcomes["successRates"];
  nationalAverage?: number;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("PublishedRatesChart");
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const publishedRates = rates.filter(
    (rate) => typeof rate.rate === "number" && rate.rate > 0,
  );
  const maxValue = 60;
  const chartHeight = 120;
  const xAxisHeight = 22;
  const barAreaHeight = chartHeight - xAxisHeight;
  const yAxisLabels = [60, 40, 20, 0];
  const chartData = publishedRates.map((rate) => {
    const comparableNationalAverage =
      typeof rate.nationalAverage === "number" && rate.nationalAverage > 0
        ? rate.nationalAverage
        : nationalAverage;
    return {
      rate,
      nationalAverage: comparableNationalAverage,
      label:
        rate.label ??
        rate.treatment ??
        (rate.ageBand === "all-ages"
          ? t("access.success_chart.all_ages")
          : rate.ageBand),
    };
  });
  const hasNationalAverages = chartData.some(
    (data) => data.nationalAverage != null,
  );
  const formatRate = (rate: number) =>
    Number.isInteger(rate) ? `${rate}%` : `${rate.toFixed(1)}%`;

  return (
    <View style={{ overflow: "hidden" }}>
      <View style={{ flexDirection: "row", gap: 8, marginBottom: 8 }}>
        <View
          style={{
            height: chartHeight,
            justifyContent: "space-between",
            paddingBottom: xAxisHeight,
          }}
        >
          {yAxisLabels.map((value) => (
            <Text
              key={value}
              className="font-dm-sans"
              style={{
                width: 28,
                fontSize: 10,
                fontWeight: "500",
                color: EMBER.colors.textFaint,
                textAlign: "right",
              }}
            >
              {value}%
            </Text>
          ))}
        </View>

        <View style={{ flex: 1, height: chartHeight, position: "relative" }}>
          <View
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: xAxisHeight,
              justifyContent: "space-between",
            }}
          >
            {yAxisLabels.map((value) => (
              <MoonDivider key={value} color="rgba(0,0,0,0.06)" />
            ))}
          </View>

          <View
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: xAxisHeight,
              flexDirection: "row",
              alignItems: "flex-end",
              justifyContent: "space-around",
              paddingHorizontal: 2,
            }}
          >
            {chartData.map((data, index) => {
              const isSelected = selectedIndex === index;
              const clinicHeight =
                (Math.min(data.rate.rate, maxValue) / maxValue) * barAreaHeight;
              const nationalHeight = data.nationalAverage
                ? (Math.min(data.nationalAverage, maxValue) / maxValue) *
                  barAreaHeight
                : 0;

              return (
                <Pressable
                  key={`${data.rate.ageBand}-${data.rate.rate}-${index}`}
                  onPress={() => {
                    capturePress(
                      pressLabel(data.rate.ageBand),
                      "published_rate_bar",
                    );
                    setSelectedIndex(isSelected ? null : index);
                  }}
                  onHoverIn={() => setSelectedIndex(index)}
                  onHoverOut={() => setSelectedIndex(null)}
                  style={{
                    flex: 1,
                    height: "100%",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  {isSelected ? (
                    <View
                      style={{
                        position: "absolute",
                        top: Math.max(
                          0,
                          Math.min(
                            barAreaHeight - clinicHeight - 34,
                            barAreaHeight - 48,
                          ),
                        ),
                        minWidth: 48,
                        borderRadius: 6,
                        backgroundColor: EMBER.colors.text,
                        paddingHorizontal: 8,
                        paddingVertical: 4,
                        zIndex: 10,
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 10,
                          fontWeight: "700",
                          color: "#FFFFFF",
                          textAlign: "center",
                        }}
                      >
                        {formatRate(data.rate.rate)}
                      </Text>
                      {data.nationalAverage ? (
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 9,
                            color: "rgba(255,255,255,0.68)",
                            textAlign: "center",
                          }}
                          numberOfLines={1}
                        >
                          {t("access.success_chart.avg_value", {
                            params: { value: formatRate(data.nationalAverage) },
                          })}
                        </Text>
                      ) : null}
                    </View>
                  ) : null}

                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 10,
                      fontWeight: "700",
                      color: EMBER.colors.text,
                      marginBottom: 4,
                    }}
                    numberOfLines={1}
                  >
                    {formatRate(data.rate.rate)}
                  </Text>

                  <View
                    style={{
                      flexDirection: "row",
                      gap: 3,
                      alignItems: "flex-end",
                    }}
                  >
                    {data.nationalAverage ? (
                      <View
                        style={{
                          width: 10,
                          height: nationalHeight,
                          borderTopLeftRadius: 2,
                          borderTopRightRadius: 2,
                          backgroundColor: isSelected
                            ? "rgba(0,0,0,0.18)"
                            : "rgba(0,0,0,0.1)",
                        }}
                      />
                    ) : null}
                    <ImageBackground
                      source={velvetCopper}
                      resizeMode="cover"
                      imageStyle={{
                        borderTopLeftRadius: 2,
                        borderTopRightRadius: 2,
                      }}
                      style={{
                        width: data.nationalAverage ? 10 : 12,
                        height: clinicHeight,
                        borderTopLeftRadius: 2,
                        borderTopRightRadius: 2,
                        overflow: "hidden",
                        opacity: isSelected ? 1 : 0.85,
                      }}
                    />
                  </View>
                </Pressable>
              );
            })}
          </View>

          <View
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: xAxisHeight,
              flexDirection: "row",
              justifyContent: "space-around",
            }}
          >
            {chartData.map((data, index) => (
              <View
                key={`${data.label}-${index}`}
                style={{ flex: 1, alignItems: "center", paddingTop: 6 }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 10,
                    fontWeight: "500",
                    color: EMBER.colors.textFaint,
                    textAlign: "center",
                  }}
                  numberOfLines={1}
                >
                  {data.label}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </View>

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: hasNationalAverages ? 16 : 6,
          paddingTop: 10,
          marginTop: 4,
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <ImageBackground
            source={velvetCopper}
            resizeMode="cover"
            imageStyle={{ borderRadius: 4 }}
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              overflow: "hidden",
            }}
          />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              fontWeight: "600",
              color: EMBER.colors.text,
            }}
          >
            {clinicName}
          </Text>
        </View>
        {hasNationalAverages ? (
          <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            <View
              style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: "rgba(0,0,0,0.12)",
              }}
            />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                fontWeight: "500",
                color: EMBER.colors.textFaint,
              }}
            >
              {t("access.success_chart.national_average")}
            </Text>
          </View>
        ) : null}
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Personalized Estimate — side-by-side comparison
// ---------------------------------------------------------------------------

export function getPredictionInfoSections(t: TranslationFunction) {
  return [
    {
      title: t("access.success_chart.info.how_title"),
      body: t("access.success_chart.info.how_body"),
    },
    {
      title: t("access.success_chart.info.adjust_title"),
      body: t("access.success_chart.info.adjust_body"),
    },
    {
      title: t("access.success_chart.info.sources_title"),
      body: t("access.success_chart.info.sources_body"),
    },
    {
      title: t("access.success_chart.info.confidence_title"),
      body: t("access.success_chart.info.confidence_body"),
    },
    {
      body: t("access.success_chart.info.disclaimer"),
    },
  ];
}

export function PersonalizedEstimate({
  prediction,
}: {
  prediction: PredictionResult;
}) {
  const t = useTranslations();
  const navigation = useNavigation();
  const hasAdjustments = prediction.adjustments.length > 0;

  return (
    <View style={{ gap: 12 }}>
      {hasAdjustments ? (
        <>
          {/* Side-by-side comparison */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            {/* Clinic rate */}
            <View style={{ flex: 1, alignItems: "center", gap: 4 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  fontWeight: "500",
                  color: EMBER.colors.textLight,
                }}
              >
                {t("access.success_chart.clinic_success_rate")}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 24,
                  fontWeight: "700",
                  color: EMBER.colors.textMid,
                }}
              >
                {Math.round(prediction.baseRate)}%
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 10,
                  color: EMBER.colors.textFaint,
                }}
              >
                {t("access.success_chart.based_on_age")}
              </Text>
            </View>

            {/* Divider */}
            <View
              style={{
                width: 1,
                height: 40,
                backgroundColor: "rgba(0,0,0,0.08)",
                marginHorizontal: 12,
              }}
            />

            {/* Your estimate */}
            <View style={{ flex: 1, alignItems: "center", gap: 4 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  fontWeight: "500",
                  color: EMBER.colors.textLight,
                }}
              >
                {t("access.success_chart.personalized_estimate")}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 24,
                  fontWeight: "800",
                  color: EMBER.colors.warm,
                }}
              >
                ~{Math.round(prediction.estimatedRate)}%
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 10,
                  color: EMBER.colors.textFaint,
                }}
              >
                {t("access.success_chart.confidence_range", {
                  params: {
                    low: String(prediction.confidenceLow),
                    high: String(prediction.confidenceHigh),
                  },
                })}
              </Text>
            </View>
          </View>

          {/* Adjustment pills — neutral brown tags */}
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 6,
            }}
          >
            {prediction.adjustments.map((adj) => (
              <View
                key={adj.factor}
                style={{
                  backgroundColor: `${EMBER.colors.textMid}14`,
                  paddingHorizontal: 8,
                  paddingVertical: 4,
                  borderRadius: 6,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11,
                    fontWeight: "600",
                    color: EMBER.colors.textMid,
                  }}
                >
                  {adj.factor}
                </Text>
              </View>
            ))}
          </View>
        </>
      ) : (
        <>
          {/* No adjustments — show base rate + CTA */}
          <View
            style={{
              gap: 12,
            }}
          >
            <SupportingSubtitleText style={{ flex: 1, marginTop: 0 }}>
              {t("access.success_chart.no_adjustments_body_prefix")}
              <Text style={{ fontWeight: "700", color: EMBER.colors.text }}>
                {Math.round(prediction.baseRate)}%
              </Text>
              {t("access.success_chart.no_adjustments_body_suffix")}
            </SupportingSubtitleText>

            {/* Compact CTA below the explanation */}
            <GlassButton
              width={174}
              shape="rounded"
              radius={10}
              label={t("access.success_chart.personalize_cta")}
              onPress={() =>
                navigation.dispatch(
                  CommonActions.navigate("Care", {
                    screen: "MedicalHistory",
                  }),
                )
              }
              backgroundColor="rgba(255,255,255,0.28)"
              borderColor="rgba(126,82,56,0.2)"
              textColor={EMBER.colors.textMid}
              trailing={
                <ChevronRight
                  size={13}
                  color={EMBER.colors.textMid}
                  strokeWidth={2}
                />
              }
              style={{ minHeight: 38, alignSelf: "flex-start" }}
              textStyle={{
                fontSize: 10,
                lineHeight: 14,
                fontWeight: "600",
              }}
            />
          </View>
        </>
      )}
    </View>
  );
}
